import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {logoutUser} from "../../redux/reducers/authSlice";
import {Link, Tooltip} from "@mui/material";
import {useRouter} from "../../hooks/useRouter";
import config from 'config';
import { useHasAnyRole } from '../../hooks/useHasAnyRole';

const pages = [
  ['My Properties', '/'],
  ['Visitor Management', '/visitors'],
  ['Guest Portal', '/bnb-guest-portal'],
];

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElAdmin, setAnchorElAdmin] = React.useState<null | HTMLElement>(null);
  const user = useAppSelector(state => state.auth.user)
  const visiblePages = pages.filter(page => !page[2] || user?.roles?.includes(page[2]));

  const router = useRouter();
  const dispatch = useAppDispatch();
  
  const currentPath = window.location.pathname;

  const canAccessAdminPortal = useHasAnyRole(['admin', 'residents-support']);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenAdminMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAdmin(event.currentTarget);
  };

  const handleCloseAdminMenu = () => {
    setAnchorElAdmin(null);
  };

  const handleLogout = () => {
    setAnchorElUser(null);
    dispatch(logoutUser()).then(() => {
      router.push('/')
    });
  }

  const handleNavClick = (path: string) => {
    setAnchorElNav(null);
    router.push(path)
  }
  
  const handleAdminNavClick = (path: string) => {
    setAnchorElAdmin(null);
    router.push(path)
  }

  return (
    <AppBar 
      position="static" 
      color="primary"
      elevation={4}
      sx={{
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 0.5 }}>
          <Box sx={{display: { xs: 'none', md: 'flex' }, mr: 1}}>
            <Link href={'/'}>
              <img
                alt="WestProp Logo"
                src={config.whiteLogo}
                style={{ marginRight: 20, width: 150 }}
              />
            </Link>

          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start' }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {visiblePages.map((page) => (
                <MenuItem 
                  key={page[0]} 
                  onClick={() => handleNavClick(page[1])}
                  sx={{
                    backgroundColor: currentPath === page[1] ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                  }}
                >
                  <Typography 
                    textAlign="center"
                    sx={{
                      fontWeight: currentPath === page[1] ? 'bold' : 'regular',
                      color: currentPath === page[1] ? 'primary.main' : 'inherit',
                    }}
                  >
                    {page[0]}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
            <img
              alt="WestProp Logo"
              src={config.whiteLogo}
              style={{ marginRight: 20, width: 100 }}
            />
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            {visiblePages.map((page) => (
              <Button
                key={page[0]}
                onClick={() => handleNavClick(page[1])}
                sx={{ 
                  my: 2, 
                  mx: 1,
                  color: 'white', 
                  display: 'block',
                  fontWeight: currentPath === page[1] ? 'bold' : 'medium',
                  borderRadius: '8px',
                  padding: '6px 16px',
                  backgroundColor: currentPath === page[1] ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  boxShadow: currentPath === page[1] ? '0 0 10px rgba(0, 0, 0, 0.1)' : 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                {page[0]}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
            {canAccessAdminPortal && (
              <>
                <Tooltip title="Admin Panel">
                  <IconButton onClick={handleOpenAdminMenu} sx={{ color: 'white', mr: 1 }}>
                    <AdminPanelSettingsIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="admin-menu-appbar"
                  anchorEl={anchorElAdmin}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElAdmin)}
                  onClose={handleCloseAdminMenu}
                >
                  <MenuItem onClick={() => handleAdminNavClick('/admin/properties')}>
                    <Typography textAlign="center">Properties</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleAdminNavClick('/admin/finance')}>
                    <Typography textAlign="center">Finance Dashboard</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleAdminNavClick('/admin/notices')}>
                    <Typography textAlign="center">Notices</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleAdminNavClick('/admin/levies')}>
                    <Typography textAlign="center">Levies</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleAdminNavClick('/admin/property-invoices')}>
                    <Typography textAlign="center">Invoices</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar>{user?.name?.slice(0,1) ?? ''}</Avatar>
            </IconButton>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem key={'logout'} onClick={handleLogout}>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;